/**
 * Picking an audio or subtitle track from a profile's language preference.
 *
 * A pure function, because the matching is the part that goes wrong and every engine
 * reports tracks in a slightly different shape. Given a list of `{ lang, name }` and a
 * two-letter preference, it answers with an index or `null` — no player, no DOM, no engine.
 *
 * ## Why the matching is fuzzy
 *
 * Nothing agrees on how to label a track. The same Turkish audio arrives as `tur`, `tr`,
 * `tr-TR`, `Turkish`, `Türkçe` or `TR AUDIO` depending on who muxed the file, and an IPTV
 * catalogue is assembled from dozens of sources. An exact `lang === 'tr'` comparison
 * matches almost nothing in practice, which reads to the viewer as the setting doing
 * nothing at all.
 *
 * So four rules are tried in order, strongest first, and the first hit wins. The order
 * matters: a track explicitly tagged `tr` must beat one merely *named* "Turkish audio",
 * because the tag is data and the name is prose.
 */

/** A track as any engine describes it, reduced to the two fields worth matching on. */
export interface TrackLike {
  /** BCP-47 or ISO 639, in whatever case and length the muxer felt like. */
  lang?: string | null;
  /** The human label, when there is one. */
  name?: string | null;
}

/**
 * ISO 639-2 and common prose, mapped to the 639-1 codes profiles are stored in.
 *
 * Only the languages the preference UI offers, plus their obvious aliases. A complete
 * table would be a maintenance burden for languages nobody in this household selects, and
 * anything missing falls through to "no match", which is the safe outcome — the stream
 * keeps its own default.
 */
export const ALIASES: Record<string, string[]> = {
  tr: ['tur', 'turkish', 'turkce', 'türkçe'],
  en: ['eng', 'english', 'ingilizce'],
  de: ['ger', 'deu', 'german', 'deutsch', 'almanca'],
  ar: ['ara', 'arabic', 'arapca', 'arapça'],
  fr: ['fre', 'fra', 'french', 'francais', 'français', 'fransizca'],
  es: ['spa', 'spanish', 'espanol', 'español', 'ispanyolca'],
  ru: ['rus', 'russian', 'russkiy', 'rusca', 'rusça'],
};

/**
 * Reduces whatever a muxer wrote in a language field to the ISO 639-1 code, or null.
 *
 * Shares {@link ALIASES} with the matcher below on purpose. The menu has to *name* the
 * language it lists and the matcher has to *find* it, and two tables would drift: a
 * stream tagged `tur` would be selected correctly by the preference and then displayed as
 * "TUR" in the menu that is supposed to explain what just happened.
 *
 * Only the two forms that carry a code are accepted — the exact code and a regional or
 * three-letter variant of it. Prose in a `lang` field ("Turkish audio") is left to the
 * label, because guessing a code from prose here would put a confident "Türkçe" on a
 * track this function is only assuming about.
 */
export function toIso1(raw: string | null | undefined): string | null {
  const value = normalise(raw);
  if (!value) return null;
  if (Object.prototype.hasOwnProperty.call(ALIASES, value)) return value;
  for (const [code, aliases] of Object.entries(ALIASES)) {
    if (value.startsWith(`${code}-`) || aliases.includes(value)) return code;
  }
  // A two-letter code outside the table is still a code, and returning it lets the label
  // fall back to showing it as-is rather than pretending the track has no language.
  //
  // The region is dropped first, or `pt-BR` would fall through to null while a bare `pt`
  // succeeded — the tagged track would be the one shown as having no language, which is
  // exactly backwards.
  const base = value.split('-')[0] ?? '';
  return /^[a-z]{2}$/.test(base) ? base : null;
}

function normalise(value: string | null | undefined): string {
  return (value ?? '')
    .toLowerCase()
    // Strip Turkish and Latin diacritics so "Türkçe" and "turkce" are the same string.
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim();
}

/**
 * @param tracks    The engine's track list, in its own order.
 * @param preferred ISO 639-1 code from the profile, or null/empty for "no preference".
 * @returns The index into `tracks`, or `null` to leave the stream's own default alone.
 */
export function pickTrackByLanguage(
  tracks: readonly TrackLike[],
  preferred: string | null | undefined,
): number | null {
  const want = normalise(preferred);
  if (!want || tracks.length === 0) return null;

  const aliases = ALIASES[want] ?? [];
  const langs = tracks.map((t) => normalise(t.lang));
  const names = tracks.map((t) => normalise(t.name));

  // 1. The tag is exactly the code. `tr`.
  let hit = langs.findIndex((l) => l === want);
  if (hit >= 0) return hit;

  // 2. The tag is a regional or three-letter form of it. `tr-TR`, `tur`.
  //    Anchored at the start so `pt-BR` can never satisfy a request for `br`.
  hit = langs.findIndex((l) => l.startsWith(`${want}-`) || aliases.includes(l));
  if (hit >= 0) return hit;

  // 3. The tag is missing but the name says it. "Türkçe", "Turkish".
  hit = names.findIndex((n) => n === want || aliases.includes(n));
  if (hit >= 0) return hit;

  /*
   * 4. The name merely contains it — "TR AUDIO", "Turkish 5.1".
   *
   * Word-bounded, and that is not fussiness: an unbounded `includes('tr')` matches
   * "Extra", "Instrumental" and "Country", so the loosest rule would be the one that
   * fires most often and it would usually be wrong.
   */
  const needles = [want, ...aliases];
  hit = names.findIndex((n) =>
    needles.some((needle) => new RegExp(`(^|[^a-z])${needle}([^a-z]|$)`).test(n)),
  );
  return hit >= 0 ? hit : null;
}
